// ALPHA-03 pricing engine. Pure, synchronous functions, no I/O and no
// persistence: this module is the read-only calculation the price-quote
// endpoint (src/api/server.ts) calls live while a ticket is being built,
// per docs/superpowers/plans/2026-08-19-alpha-work-breakdown.md.
//
// Every dollar amount here is an integer number of cents. Every pour
// amount is a decimal string (e.g. "1.5", "2"), never a JS `number`,
// because a binary float cannot represent every decimal fraction exactly
// (0.1 has no exact float representation) and this module must not lose
// or drift a cent through that representation gap. Ounces are parsed into
// a scaled integer (hundredths of an ounce) and all downstream
// multiplication happens on BigInt, so no money or pour quantity in this
// file ever passes through IEEE-754 arithmetic.
//
// Rounding and tax follow docs/decision-proposals/provisional-pricing-config.md
// exactly: each line is computed at full precision then rounded once,
// half-up, to the cent; the subtotal is the exact sum of the already
// -rounded lines; tax is computed on that subtotal at the configured
// basis-point rate and rounded half-up to the cent. Both the tax rate and
// the rounding mode are provisional (pending DEC-004 and DEC-006), which
// is why every value this module returns carries `provisional: true`.

import { ValidationError } from "./errors.ts";
import { normalizeLeadingDot } from "./leadingDot.ts";
// VOC1-F9 (docs/build-briefs/VOC1-F9-price-rounding-mode-location-
// configurable.md). Type-only import: src/domain/tenantSettings.ts imports
// parseOuncesToHundredths from this module, so a runtime import back would
// be circular; a `type`-only import is erased entirely before anything
// runs, so no cycle exists at runtime. ROUNDING_MODES/RoundingMode's one
// canonical home stays tenantSettings.ts (that module's own comment records
// why it is hand-mirrored against src/config/pricing.ts rather than a third
// copy living here too).
import type { RoundingMode } from "./tenantSettings.ts";

export interface PricingModifier {
  readonly label: string;
  readonly amountCents: number;
}

export interface PriceLineInput {
  readonly perOuncePriceCents: number;
  /** Decimal string, e.g. "1.5" or "2". At most two decimal places. */
  readonly ounces: string;
  readonly buildChargeCents: number;
  readonly modifiers?: readonly PricingModifier[];
  /** VOC1-F9. The tenant's configured rounding rule for this line's one
   * rounding step. Optional and defaults to DEFAULT_ROUNDING_MODE
   * ("half-up-cent", the sole DEC-006-approved mode) so a caller that has
   * not yet been updated to thread the tenant's own stored
   * getEffectiveTenantSettings(...).rounding_mode
   * (src/domain/lineResolution.ts, src/domain/inventorySale.ts) keeps
   * today's exact behavior unchanged. */
  readonly roundingMode?: RoundingMode;
}

export interface PricedLine {
  readonly lineTotalCents: number;
  readonly provisional: true;
}

export interface OrderTotals {
  readonly subtotalCents: number;
  readonly taxCents: number;
  readonly totalCents: number;
  readonly provisional: true;
}

// Inventory track (track/inventory) Slice 3, Amendment 2026-08-22
// (thirteenth): "neat and rocks pour pricing follows the location's
// configured pricing model... Under the shelf model: the flat price of the
// bottle's shelf tier... one price per pour regardless of recipe-free
// simplicity." Also used for a 'packaged' item's flat unit price (Amendment
// twelfth, item 3: "sold as-is"), which never varies by pricing model at
// all. Both cases share the identical shape: a single already-known flat
// price in integer cents, plus the same optional modifiers priceLine
// accepts, with no ounce math involved at all. Deliberately NOT a variant
// of priceLine (which always multiplies by ounces): a flat price is not "1
// ounce at some rate," it is a different pricing shape entirely, and
// forcing it through priceLine's ounce parameter would misrepresent what
// is actually being charged.
export interface PriceFlatInput {
  readonly flatPriceCents: number;
  readonly modifiers?: readonly PricingModifier[];
}

/**
 * Prices one flat-rate line (a shelf-tier pour, or a packaged item's unit
 * price) as flatPriceCents plus the sum of any modifiers. Every operand is
 * already an exact integer number of cents, so no rounding step is needed
 * (contrast priceLine, which must round once because ounces * per-ounce
 * cents is not generally an integer number of cents on its own); this
 * function still validates every input the same way priceLine does, so a
 * negative or non-integer flat price or modifier is rejected here rather
 * than surfacing as a database error later.
 */
export function priceFlat(input: PriceFlatInput): PricedLine {
  requireIntegerCents(input.flatPriceCents, "flatPriceCents");
  if (input.flatPriceCents < 0) {
    throw new ValidationError("flatPriceCents must not be negative");
  }
  const modifiers = input.modifiers ?? [];
  let totalCentsBig = BigInt(input.flatPriceCents);
  for (const modifier of modifiers) {
    requireIntegerCents(modifier.amountCents, `modifier "${modifier.label}" amountCents`);
    totalCentsBig += BigInt(modifier.amountCents);
  }
  return { lineTotalCents: toSafeCents(totalCentsBig, "lineTotalCents"), provisional: true };
}

// -----------------------------------------------------------------------
// Half-up rounding, explicit and sign-aware
// -----------------------------------------------------------------------
//
// `Math.round` is never used on a money path in this module. Two reasons:
//
// 1. `Math.round` operates on `number`, and every value here is either an
//    exact integer already or a BigInt built from scaled integers; routing
//    either through a float to round it reopens exactly the precision gap
//    this module exists to close.
// 2. `Math.round` rounds -0.5 to -0 (i.e. toward positive infinity, the
//    same direction as its positive-side "half up"), which is a legitimate
//    convention but not the one this module documents and tests. This
//    module instead treats a tie as rounding the *magnitude* up and
//    reapplying the sign ("round half away from zero"): -1.5 cents rounds
//    to -2 cents, exactly as +1.5 cents rounds to +2 cents. That symmetry
//    matters because modifiers may be negative (discounts): a discount
//    line that lands on an exact half-cent tie rounds the discount further
//    in the customer's favor, the same way a charge on a tie rounds
//    further against them, rather than the two cases resolving
//    inconsistently depending on which side of zero the tie fell on. See
//    test/pricing.test.ts's negative-modifier half-cent case, which
//    documents this choice against what `Math.round` would have done.
//
// halfUpDivide(numerator, denominator) rounds numerator/denominator to the
// nearest integer BigInt, half ties away from zero. denominator must be
// positive. Exported for src/domain/tips.ts's own basis-point percentage
// math (ALPHA-08's tip presets): the same half-up-cent rounding rule this
// module already applies to tax applies to a preset tip percentage of the
// subtotal, so this is reused rather than re-implemented a second time.
export function halfUpDivide(numerator: bigint, denominator: bigint): bigint {
  if (denominator <= 0n) {
    throw new RangeError("halfUpDivide requires a positive denominator");
  }
  const negative = numerator < 0n;
  const magnitude = negative ? -numerator : numerator;
  const quotient = magnitude / denominator;
  const remainder = magnitude % denominator;
  const roundedMagnitude = remainder * 2n >= denominator ? quotient + 1n : quotient;
  return negative ? -roundedMagnitude : roundedMagnitude;
}

// VOC1-F9. The tenant's stored rounding_mode is now genuinely consulted
// here rather than validated once at config-load time and then discarded
// (the exact defect this item fixes). One case today, calling halfUpDivide
// unchanged, so every existing price stays byte-identical; a value outside
// RoundingMode's own type (a corrupted stored value, or a caller that
// bypassed validateRoundingMode) falls to the switch's own default and
// throws rather than silently running half-up-cent regardless of what was
// configured, matching this module's fail-loud philosophy for the tax rate
// (src/config/pricing.ts) and tenant settings generally. No `default` case
// is written for the one real mode; TypeScript's own exhaustiveness check
// on the `never`-typed fallthrough is what forces this switch to be
// revisited the moment a second mode is ever approved and added to
// ROUNDING_MODES, rather than that addition silently compiling as a no-op.
export const DEFAULT_ROUNDING_MODE: RoundingMode = "half-up-cent";

function roundMoney(numerator: bigint, denominator: bigint, roundingMode: RoundingMode): bigint {
  switch (roundingMode) {
    case "half-up-cent":
      return halfUpDivide(numerator, denominator);
    default: {
      // Assigning to a `never`-typed binding is what makes this a real
      // exhaustiveness check: if ROUNDING_MODES ever gains a second value
      // without a matching `case` above, roundingMode's narrowed type here
      // stops being `never` and this line fails to compile, rather than
      // silently reaching this branch at runtime for a mode nobody wired.
      const exhaustiveCheck: never = roundingMode;
      throw new ValidationError(`"${String(exhaustiveCheck)}" is not a recognized rounding mode`);
    }
  }
}

// -----------------------------------------------------------------------
// Ounce parsing: decimal string -> hundredths of an ounce (BigInt)
// -----------------------------------------------------------------------

const OUNCES_PATTERN = /^\d+(\.\d{1,2})?$/;

// Non-negative decimal strings with at most two decimal places, e.g.
// "0.25", "1.5", "2". Per the ALPHA-03 spec: parse to hundredths of an
// ounce (the precision pour amounts are actually specified at), multiply
// by the per-ounce price, then divide back by 100 with explicit half-up
// rounding on the remainder. A third decimal digit is rejected rather
// than silently truncated or rounded away here, so a caller that meant
// "1.567" gets a clear 400 instead of a quietly wrong price.
// Exported so callers that must validate an ounces string BEFORE reaching
// this module's own money math (src/domain/inventorySale.ts's shelf-model
// branch, which never calls priceLine at all) can enforce the identical
// format/non-negative/precision rule rather than a second, drifting copy.
// See that module's own comment on why this became necessary.
export function parseOuncesToHundredths(ounces: string): bigint {
  const trimmed = normalizeLeadingDot(ounces);
  if (!OUNCES_PATTERN.test(trimmed)) {
    throw new ValidationError(
      `ounces must be a non-negative decimal string with at most two decimal places ` +
        `(e.g. "1.5"); received "${ounces}"`,
    );
  }
  const [wholePart, fractionalPartRaw] = trimmed.split(".");
  const fractionalPart = (fractionalPartRaw ?? "").padEnd(2, "0");
  // wholePart is always defined: OUNCES_PATTERN requires at least one
  // leading digit before any optional decimal point.
  return BigInt(wholePart as string) * 100n + BigInt(fractionalPart);
}

function requireIntegerCents(value: number, label: string): number {
  if (!Number.isInteger(value)) {
    throw new ValidationError(`${label} must be an integer number of cents; received ${value}`);
  }
  return value;
}

// -----------------------------------------------------------------------
// priceLine
// -----------------------------------------------------------------------

/**
 * Prices one order line at full precision, then rounds once, half-up, to
 * the cent.
 *
 * The line's raw value is carried in units of 1/100 of a cent (never a
 * float) until the single final rounding step: `hundredthsOfOunce *
 * perOuncePriceCents` is already exact in those units (both operands are
 * exact integers), and `buildChargeCents` plus each modifier's
 * `amountCents` are already whole cents, so scaling them by 100 to join
 * the same unit introduces no additional rounding. That single combined
 * value is rounded once, which is mathematically identical to rounding
 * the pour cost alone and then adding the already-integer build charge
 * and modifiers, but says so in the code the same way the binding spec
 * states it: one full-precision line, rounded once.
 */

// The single guarded BigInt-to-number boundary. Anything beyond
// Number.MAX_SAFE_INTEGER cents cannot be represented faithfully as a
// JS number, so it fails LOUDLY here instead of silently losing cents,
// per this module's fail-loud philosophy (ALPHA-03 gate, Critical).
// Exported so every later BigInt-sum boundary in this codebase (ALPHA-08's
// src/domain/tips.ts and src/domain/closeout.ts) applies the identical
// guard rather than a re-implemented copy.
export function toSafeCents(value: bigint, label: string): number {
  if (value > BigInt(Number.MAX_SAFE_INTEGER) || value < -BigInt(Number.MAX_SAFE_INTEGER)) {
    throw new ValidationError(`${label} exceeds the safe integer range and cannot be represented`);
  }
  return Number(value);
}

export function priceLine(input: PriceLineInput): PricedLine {
  requireIntegerCents(input.perOuncePriceCents, "perOuncePriceCents");
  if (input.perOuncePriceCents < 0) {
    throw new ValidationError("perOuncePriceCents must not be negative");
  }
  requireIntegerCents(input.buildChargeCents, "buildChargeCents");
  if (input.buildChargeCents < 0) {
    throw new ValidationError("buildChargeCents must not be negative");
  }

  const modifiers = input.modifiers ?? [];
  // BigInt accumulation, not plain number addition: the ALPHA-03 gate
  // proved the earlier `number` loop silently lost cents once the sum
  // crossed Number.MAX_SAFE_INTEGER, contradicting this module's own
  // no-float invariant. Every money sum in this file is BigInt until
  // the single guarded conversion at the boundary.
  let modifiersCents = 0n;
  for (const modifier of modifiers) {
    requireIntegerCents(modifier.amountCents, `modifier "${modifier.label}" amountCents`);
    modifiersCents += BigInt(modifier.amountCents);
  }

  const hundredthsOfOunce = parseOuncesToHundredths(input.ounces);
  const pourRawHundredthsCent = hundredthsOfOunce * BigInt(input.perOuncePriceCents);
  const flatRawHundredthsCent = (BigInt(input.buildChargeCents) + modifiersCents) * 100n;
  const totalRawHundredthsCent = pourRawHundredthsCent + flatRawHundredthsCent;

  const roundingMode = input.roundingMode ?? DEFAULT_ROUNDING_MODE;
  const lineTotalCents = toSafeCents(
    roundMoney(totalRawHundredthsCent, 100n, roundingMode),
    "lineTotalCents",
  );
  return { lineTotalCents, provisional: true };
}

// -----------------------------------------------------------------------
// orderTotals
// -----------------------------------------------------------------------

// VOC1-F6 (docs/build-briefs/VOC1-F6-per-line-tax-treatment-snapshot.md;
// DEC-169, ruled option A). The single tax-treatment value every
// order_lines row snapshots at add-line time (db/schema.ts's orderLines.
// taxTreatment, migration 0121), because this product supports exactly one
// flat, order-level tax rate (orderTotals below computes it once, on the
// summed subtotal). The single source both add-line paths read; no caller
// chooses a value, because none exists to choose. The vocabulary itself is
// owned by DEC-004; a second value arrives only by a later migration once
// that decision defines one.
export const STANDARD_TAX_TREATMENT = "standard";

/**
 * Sums already-rounded lines to a subtotal, then computes tax on that
 * subtotal at the given basis-point rate, rounded half-up to the cent.
 *
 * `taxRateBasisPoints` comes from PricingConfig.taxRateBasisPoints
 * (src/config/pricing.ts); it is threaded in as a plain number here
 * rather than the whole PricingConfig so this function stays a pure
 * calculation over its inputs, independent of how the caller loaded its
 * configuration.
 *
 * `roundingMode` (VOC1-F9) governs the one rounding step this function
 * itself performs (tax on the subtotal); it does not re-round any line,
 * since every line arrives already rounded. Optional, defaulting to
 * DEFAULT_ROUNDING_MODE, so an existing two-argument call
 * (test/pricing.test.ts, src/domain/businessDayCapture.ts,
 * src/domain/discountMath.ts's own default) keeps today's exact behavior.
 */
export function orderTotals(
  lines: readonly PricedLine[],
  taxRateBasisPoints: number,
  roundingMode: RoundingMode = DEFAULT_ROUNDING_MODE,
): OrderTotals {
  if (!Number.isInteger(taxRateBasisPoints) || taxRateBasisPoints < 0) {
    throw new ValidationError(
      `taxRateBasisPoints must be a non-negative integer number of basis points; received ${taxRateBasisPoints}`,
    );
  }

  let subtotalCentsBig = 0n;
  for (const line of lines) {
    if (!Number.isInteger(line.lineTotalCents)) {
      throw new ValidationError("every line total must be an integer number of cents");
    }
    subtotalCentsBig += BigInt(line.lineTotalCents);
  }
  const subtotalCents = toSafeCents(subtotalCentsBig, "subtotalCents");

  // Basis points are parts per 10,000 (725 basis points = 7.25% =
  // 725/10000). subtotalCents * taxRateBasisPoints stays an exact
  // integer (both operands are exact integers), so it is promoted to
  // BigInt only for this one division; no float is ever involved.
  const taxCents = toSafeCents(
    roundMoney(BigInt(subtotalCents) * BigInt(taxRateBasisPoints), 10_000n, roundingMode),
    "taxCents",
  );

  return {
    subtotalCents,
    taxCents,
    totalCents: toSafeCents(BigInt(subtotalCents) + BigInt(taxCents), "totalCents"),
    provisional: true,
  };
}

/**
 * DUALPRICE1. cents x basisPoints / 10,000 through the same rounding step orderTotals uses for tax, so the card price and the
 * tax on it round exactly as tax does today (half-up to the cent by default). 270 basis points of 1000 cents is 27 cents.
 */
export function basisPointsOfCents(
  cents: number,
  basisPoints: number,
  roundingMode: RoundingMode = DEFAULT_ROUNDING_MODE,
): number {
  if (!Number.isInteger(cents) || cents < 0) {
    throw new ValidationError("cents must be a non-negative integer");
  }
  if (!Number.isInteger(basisPoints) || basisPoints < 0) {
    throw new ValidationError("basisPoints must be a non-negative integer");
  }
  return toSafeCents(roundMoney(BigInt(cents) * BigInt(basisPoints), 10_000n, roundingMode), "basisPointsOfCents");
}
